/* Native Shopify behavior; no third-party theme or bundle-app runtime. */
(() => {
  const config = window.BloomTheme;
  // Shopify CLI cannot proxy the automatic Shop Pay redirect on loopback hosts.
  const localPreview = window.location.hostname === '127.0.0.1';
  const checkoutUrl = () => `${config.root}checkout${localPreview ? '?skip_shop_pay=true&auto_redirect=false' : ''}`;
  const announce = (text) => { const status = document.querySelector('[data-theme-status]'); if (status) status.textContent = text; };
  function bonusEligibleBottles(items) {
    return items.reduce((sum, item) => {
      // Keep existing packed-variant carts eligible when they predate this metadata.
      const legacyPack = { 49437419962465: 4, 49436270198881: 5 }[item.variant_id];
      const properties = item.properties || {};
      const eligible = properties._bp_protocol_eligible === '1'
        || (properties._bp_protocol_eligible == null && legacyPack);
      if (!eligible) return sum;
      const bottles = Number(properties._bp_bottles_per_unit || legacyPack || 1);
      return sum + (Number.isInteger(bottles) && bottles > 0 ? bottles * item.quantity : 0);
    }, 0);
  }
  class BloomGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbnails = [...this.querySelectorAll('[data-thumbnail]')];
      this.rail = this.querySelector('.bp-gallery-thumbnails');
      if (!this.viewport || !this.slides.length || !this.rail) return;
      this.initialized = true;
      this.index = this.index || 0;
      this.pendingIndex = null;
      this.viewportWidth = this.viewport.clientWidth;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      const listen = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal: this.controller.signal });
      this.syncRailButtons = () => {
        this.querySelector('[data-thumbnail-prev]').disabled = this.rail.scrollLeft <= 1;
        this.querySelector('[data-thumbnail-next]').disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 1;
      };
      listen(this.rail, 'scroll', this.syncRailButtons, { passive: true });
      this.railObserver = new ResizeObserver(() => {
        const width = this.viewport.clientWidth;
        if (width && Math.abs(width - this.viewportWidth) > 0.5) {
          this.viewportWidth = width;
          this.show(this.pendingIndex ?? this.index, { behavior: 'auto' });
        }
        this.syncRailButtons();
      });
      this.railObserver.observe(this.viewport);
      this.railObserver.observe(this.rail);
      listen(this, 'click', (event) => {
        const thumbnail = event.target.closest('[data-thumbnail]');
        if (thumbnail) this.show(Number(thumbnail.dataset.thumbnail));
        if (event.target.closest('[data-gallery-next]')) this.show((this.pendingIndex ?? this.index) + 1);
        if (event.target.closest('[data-gallery-prev]')) this.show((this.pendingIndex ?? this.index) - 1);
        if (event.target.closest('[data-thumbnail-next],[data-thumbnail-prev]')) {
          const direction = event.target.closest('[data-thumbnail-next]') ? 1 : -1;
          this.rail.scrollBy({ left: this.rail.clientWidth * direction, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
        }
      });
      listen(this, 'keydown', (event) => {
        let target = this.pendingIndex ?? this.index;
        if (event.key === 'ArrowRight') target++;
        else if (event.key === 'ArrowLeft') target--;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = this.slides.length - 1;
        else return;
        event.preventDefault();
        this.show(target);
      });
      listen(this.viewport, 'scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.finishScroll(), 160);
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          if (this.pendingIndex === null) {
            const nearest = this.nearestIndex();
            if (nearest !== this.index) this.setActive(nearest);
          }
        });
      }, { passive: true });
      listen(this.viewport, 'scrollend', () => this.finishScroll());
      // Touch uses the browser's native momentum and scroll snap. Only mouse dragging needs a handler.
      listen(this.viewport, 'pointerdown', (event) => {
        this.pendingIndex = null;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag = { id: event.pointerId, x: event.clientX, left: this.viewport.scrollLeft, index: this.nearestIndex(), active: false };
      });
      listen(this.viewport, 'pointermove', (event) => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = this.drag.x - event.clientX;
        if (!this.drag.active && Math.abs(distance) < 6) return;
        if (!this.drag.active) {
          this.drag.active = true;
          this.viewport.classList.add('is-dragging');
          this.viewport.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        const delta = Math.max(-this.viewportWidth, Math.min(distance, this.viewportWidth));
        this.viewport.scrollLeft = this.drag.left + delta;
      });
      const finishDrag = (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        this.drag = null;
        this.viewport.classList.remove('is-dragging');
        if (!drag.active) return;
        if (this.viewport.hasPointerCapture?.(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
        const distance = this.viewport.scrollLeft - drag.left;
        const committed = event.type !== 'pointercancel' && Math.abs(distance) > Math.min(60, this.viewportWidth * 0.15);
        this.show(committed ? drag.index + Math.sign(distance) : drag.index);
      };
      listen(this.viewport, 'pointerup', finishDrag);
      listen(this.viewport, 'pointercancel', finishDrag);
      if (window.Shopify?.designMode) {
        listen(this, 'shopify:block:select', (event) => {
          const index = this.slides.indexOf(event.target.closest('[data-slide]'));
          if (index !== -1) this.show(index);
        });
      }
      this.show(this.index, { behavior: 'auto' });
      this.syncRailButtons();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.railObserver?.disconnect();
      clearTimeout(this.scrollTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.viewport?.classList.remove('is-dragging');
      this.drag = null;
      this.scrollFrame = null;
      this.initialized = false;
    }
    slideOffset(index) { return this.slides[index].offsetLeft - this.slides[0].offsetLeft; }
    nearestIndex() {
      return this.slides.reduce((nearest, _, index) => Math.abs(this.slideOffset(index) - this.viewport.scrollLeft) < Math.abs(this.slideOffset(nearest) - this.viewport.scrollLeft) ? index : nearest, 0);
    }
    finishScroll() {
      if (this.drag?.active) return;
      clearTimeout(this.scrollTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.pendingIndex = null;
      this.setActive(this.nearestIndex(), true);
    }
    show(index, { behavior = 'smooth' } = {}) {
      if (!this.slides?.length) return;
      const target = Math.max(0, Math.min(Math.round(Number(index) || 0), this.slides.length - 1));
      const left = this.slideOffset(target);
      const motion = this.reducedMotion.matches ? 'auto' : behavior;
      this.pendingIndex = motion === 'smooth' && Math.abs(left - this.viewport.scrollLeft) > 1 ? target : null;
      this.setActive(target, true);
      this.viewport.scrollTo({ left, behavior: motion });
    }
    setActive(index, announce = false) {
      this.index = index;
      this.querySelector('[data-gallery-prev]').disabled = index === 0;
      this.querySelector('[data-gallery-next]').disabled = index === this.slides.length - 1;
      this.slides.forEach((slide, i) => slide.setAttribute('aria-hidden', String(i !== index)));
      this.thumbnails.forEach((thumbnail, i) => thumbnail.setAttribute('aria-pressed', String(i === index)));
      const count = this.querySelector('[data-gallery-current]');
      if (count) count.textContent = String(index + 1);
      const status = this.querySelector('[data-gallery-status]');
      if (announce && status) status.textContent = `Image ${index + 1} of ${this.slides.length}`;
      // Load the adjacent real images before a swipe; no cloned preview or image replacement.
      this.slides.slice(Math.max(0, index - 1), index + 2).forEach(slide => {
        const image = slide.querySelector('img');
        if (image?.loading === 'lazy') image.loading = 'eager';
      });
      const thumbnail = this.thumbnails[index];
      if (!thumbnail) return;
      const padding = parseFloat(getComputedStyle(this.rail).paddingLeft) || 0;
      const start = thumbnail.getBoundingClientRect().left - this.rail.getBoundingClientRect().left + this.rail.scrollLeft;
      const end = start + thumbnail.offsetWidth;
      if (start < this.rail.scrollLeft + padding) this.rail.scrollTo({ left: Math.max(0, start - padding), behavior: 'auto' });
      else if (end > this.rail.scrollLeft + this.rail.clientWidth - padding) this.rail.scrollTo({ left: end - this.rail.clientWidth + padding, behavior: 'auto' });
    }
  }
  customElements.define('bp-gallery', BloomGallery);
  // A session reminder, not an inventory hold or an expiring discount.
  let cartCountdownDeadline = null;
  class BloomCartCountdown extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.storageKey = 'bp-cart-countdown:v1';
      this.clock = this.querySelector('[data-cart-countdown-clock]');
      this.label = this.querySelector('[data-cart-countdown-label]');
      this.drawer = this.closest('dialog');
      this.duration = Math.max(1, Number(this.dataset.duration) || 600) * 1000;
      if (this.hidden) {
        cartCountdownDeadline = null;
        try { sessionStorage.removeItem(this.storageKey); } catch (_) {}
        return;
      }
      if (!this.drawer || !this.clock || !this.label) return;
      try { cartCountdownDeadline ??= Number(sessionStorage.getItem(this.storageKey)) || null; } catch (_) {}
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
      this.visibilityHandler = () => this.update();
      document.addEventListener('visibilitychange', this.visibilityHandler);
      this.update();
    }
    update() {
      clearInterval(this.interval);
      this.interval = null;
      if (!this.drawer.open) return;
      const now = Date.now();
      if (!Number.isFinite(cartCountdownDeadline) || cartCountdownDeadline > now + this.duration) cartCountdownDeadline = null;
      if (!cartCountdownDeadline) {
        cartCountdownDeadline = now + this.duration;
        try { sessionStorage.setItem(this.storageKey, String(cartCountdownDeadline)); } catch (_) {}
      }
      if (this.render()) this.interval = setInterval(() => {
        if (!this.render()) { clearInterval(this.interval); this.interval = null; }
      }, 1000);
    }
    render() {
      const seconds = Math.max(0, Math.ceil((cartCountdownDeadline - Date.now()) / 1000));
      this.clock.hidden = seconds === 0;
      this.label.textContent = seconds ? 'We can only reserve your cart for' : 'Your cart is still saved — checkout anytime';
      this.clock.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
      return seconds > 0;
    }
    disconnectedCallback() {
      clearInterval(this.interval);
      this.observer?.disconnect();
      this.observer = null;
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }
  customElements.define('bp-cart-countdown', BloomCartCountdown);
  async function refreshCart() {
    const response = await fetch(`${config.root}?sections=bp-cart-drawer`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to update your cart. Please refresh the page.');
    const sections = await response.json();
    if (!sections['bp-cart-drawer']) throw new Error('Unable to render the cart.');
    const parsed = new DOMParser().parseFromString(sections['bp-cart-drawer'], 'text/html');
    const content = parsed.querySelector('[data-cart-content]');
    if (!content) throw new Error('Unable to render the cart.');
    const drawer = document.querySelector('#BloomCart');
    const currentContent = drawer.querySelector('[data-cart-content]');
    const active = document.activeElement;
    const focusedRow = currentContent.contains(active) ? active.closest('[data-line-key]') : null;
    const focusedKey = focusedRow?.dataset.lineKey;
    const focusedControl = focusedRow ? Array.from(focusedRow.querySelectorAll('a,button,input')).indexOf(active) : -1;
    currentContent.replaceChildren(...content.childNodes);
    const cartResponse = await fetch(`${config.cartUrl}.js`);
    if (cartResponse.ok) {
      const cart = await cartResponse.json();
      document.querySelectorAll('[data-cart-count]').forEach((badge) => { badge.textContent = cart.item_count; badge.hidden = cart.item_count === 0; });
    }
    return () => {
      if (!focusedKey || !drawer.open || (document.activeElement !== document.body && document.activeElement !== drawer)) return;
      const row = Array.from(currentContent.querySelectorAll('[data-line-key]')).find(item => item.dataset.lineKey === focusedKey);
      const control = row?.querySelectorAll('a,button,input')[focusedControl];
      const target = control || currentContent.querySelector('.bp-drawer-checkout') || drawer.querySelector('[data-cart-close]');
      if (target && !target.disabled) target.focus({ preventScroll: true });
    };
  }
  class BloomProductForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.subscriptionData = JSON.parse(this.querySelector('[data-subscription-data]')?.textContent || '{}');
      this.subscriptionToggle = this.querySelector('[data-subscription-toggle]');
      this.subscriptionFrequency = this.querySelector('[data-subscription-frequency]');
      this.subscriptionLabel = this.querySelector('[data-subscription-label]');
      this.subscriptionOriginalLabel = this.subscriptionLabel?.textContent;
      this.updateSubscription();
      this.updateBonusAvailability();
      this.updateOfferImage();
      this.form.addEventListener('change', (event) => {
        if (event.target.name === 'id') {
          this.querySelectorAll('.bp-bundle').forEach((label) => label.classList.toggle('is-selected', label.contains(event.target)));
          this.updateSubscription();
          this.updateBonusAvailability();
          this.updateOfferImage();
        }
        if (event.target === this.subscriptionToggle || event.target === this.subscriptionFrequency) this.updateSubscriptionPrices();
      });
      this.form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = event.submitter || this.form.querySelector('[type=submit]');
        const error = this.querySelector('[role=alert]');
        button.disabled = true; button.setAttribute('aria-busy', 'true'); error.hidden = true;
        try {
          const data = new FormData(this.form);
          const selectedOffer = this.form.querySelector('[name="id"]:checked')?.closest('.bp-bundle');
          const offer = selectedOffer?.querySelector('.bp-bundle-title').textContent.trim() || '';
          const gifts = JSON.parse(this.querySelector('[data-gift-products]')?.textContent || '[]')
            .filter(gift => !gift.minimum || Number(selectedOffer?.dataset.bottles || 0) >= gift.minimum);
          const properties = {};
          for (const [key, value] of data) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) properties[match[1]] = value;
          }
          if (offer) properties.Offer = offer;
          if (selectedOffer?.dataset.shippingBenefit) properties['Delivery benefit'] = selectedOffer.dataset.shippingBenefit;
          properties._bp_protocol_eligible = Number(selectedOffer?.dataset.bottles || 0) >= Number(this.dataset.bonusMinimum || 4) ? '1' : '0';
          properties._bp_bottles_per_unit = String(Number(selectedOffer?.dataset.bottles || 1));
          const paidItem = { id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1), properties };
          if (data.get('selling_plan')) paidItem.selling_plan = Number(data.get('selling_plan'));
          let existingGifts = new Set();
          if (gifts.length) {
            const currentResponse = await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } });
            if (!currentResponse.ok) throw new Error('Unable to check your cart. Please try again.');
            const current = await currentResponse.json();
            existingGifts = new Set(current.items.filter(item => item.properties?._bp_bonus === '1').map(item => item.variant_id));
          }
          const purchasedItems = selectedOffer?.dataset.bogoUnit && !paidItem.selling_plan
            ? [
              { id: Number(selectedOffer.dataset.bogoUnit), quantity: Number(selectedOffer.dataset.bogoPaid) - (selectedOffer.dataset.bogoAdjustment ? 1 : 0), properties: { ...properties, _bp_bogo: 'paid', _bp_bottles_per_unit: '1' } },
              { id: Number(selectedOffer.dataset.bogoFreeUnit || selectedOffer.dataset.bogoUnit), quantity: Number(selectedOffer.dataset.bogoFree), properties: { ...properties, _bp_bogo: 'free', _bp_bottles_per_unit: '1' } }
            ] : [paidItem];
          if (selectedOffer?.dataset.bogoAdjustment && !paidItem.selling_plan) purchasedItems.push({ id: Number(selectedOffer.dataset.bogoAdjustment), quantity: 1, properties: { ...properties, _bp_bogo: 'paid', _bp_protocol_eligible: '1', _bp_bottles_per_unit: '1' } });
          const items = [...purchasedItems, ...gifts.filter(gift => !existingGifts.has(gift.id)).map(gift => ({
            id: gift.id, quantity: 1, properties: { _bp_bonus: '1', _bp_parent: gift.parent, _bp_minimum: String(gift.minimum || 0), Offer: offer }
          }))];
          const response = await fetch(config.cartAddUrl, { method: 'POST', body: JSON.stringify({ items }), headers: { Accept: 'application/json', 'Content-Type': 'application/json' } });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'Unable to add this item. Please try again.');
          if (button.hasAttribute('data-checkout')) {
            window.location.assign(checkoutUrl());
            return;
          }
          await refreshCart();
          document.querySelector('#BloomCart').showModal();
          announce('Added to cart');
        } catch (failure) { error.textContent = failure.message; error.hidden = false; }
        finally { button.disabled = false; button.removeAttribute('aria-busy'); }
      });
    }
    updateOfferImage() {
      const offer = this.form.querySelector('[name="id"]:checked')?.closest('.bp-bundle');
      if (!offer) return;
      const gallery = this.closest('.bp-product')?.querySelector('bp-gallery');
      const slide = gallery?.querySelector('[data-slide="0"]');
      const art = slide?.querySelector('[data-offer-art]');
      if (art) {
        const bottles = Number(offer.dataset.bottles);
        const free = Number(offer.dataset.freeBottles);
        art.dataset.bottleCount = String(bottles);
        art.setAttribute('aria-label', `${offer.querySelector('.bp-bundle-title').textContent.trim()} — ${bottles} bottles`);
        art.querySelector('[data-offer-art-title]').textContent = offer.querySelector('.bp-bundle-title').textContent;
        art.querySelector('[data-offer-art-shipping]').textContent = offer.dataset.shippingBenefit || '';
        art.querySelectorAll('[data-offer-art-bottle]').forEach(image => { image.hidden = Number(image.dataset.offerArtBottle) > bottles; });
        const badge = art.querySelector('[data-offer-art-free]');
        badge.hidden = free === 0;
        badge.textContent = `${free} ${free === 1 ? 'bottle' : 'bottles'} FREE`;
        if (gallery.initialized) gallery.show(0, { behavior: 'auto' });
        return;
      }
      const image = slide?.querySelector('img');
      if (!image || !offer.dataset.offerImage) return;
      image.removeAttribute('srcset');
      image.src = offer.dataset.offerImage;
      image.alt = offer.dataset.offerAlt || '';
      slide.setAttribute('aria-label', `1 of ${gallery.querySelectorAll('[data-slide]').length}`);
      const thumbnail = gallery.querySelector('[data-thumbnail="0"] img');
      if (thumbnail) { thumbnail.removeAttribute('srcset'); thumbnail.src = image.src; }
      const stickyImage = this.closest('[id^="shopify-section-"]')?.querySelector('.bp-ev-sticky-inner > img');
      if (stickyImage) { stickyImage.removeAttribute('srcset'); stickyImage.src = image.src; stickyImage.alt = image.alt; }
      if (gallery.initialized) { gallery.show(0, { behavior: 'auto' }); }
    }
    updateBonusAvailability() {
      const bottles = Number(this.form.querySelector('[name="id"]:checked')?.closest('.bp-bundle')?.dataset.bottles || 0);
      this.querySelectorAll('[data-bonus-minimum]').forEach(card => {
        const locked = bottles < Number(card.dataset.bonusMinimum);
        card.querySelector('p').textContent = locked ? (this.dataset.bonusLockedLabel || 'Select a higher bundle') : card.dataset.bonusTitle;
        const image = card.querySelector('img');
        if (image) image.hidden = locked;
        card.classList.toggle('is-locked', locked);
      });
    }
    updateSubscription() {
      if (!this.subscriptionToggle) return;
      const variant = this.subscriptionData[this.form.querySelector('[name="id"]:checked')?.value];
      const plans = variant?.plans || [];
      const previous = this.subscriptionToggle.value;
      this.subscriptionFrequency.replaceChildren(...plans.map(plan => new Option(plan.name, String(plan.id))));
      if (plans.some(plan => String(plan.id) === previous)) this.subscriptionFrequency.value = previous;
      this.subscriptionToggle.disabled = plans.length === 0;
      if (!plans.length) this.subscriptionToggle.checked = false;
      this.updateSubscriptionPrices();
    }
    updateSubscriptionPrices() {
      if (!this.subscriptionToggle) return;
      const variant = this.subscriptionData[this.form.querySelector('[name="id"]:checked')?.value];
      const plan = variant?.plans.find(item => String(item.id) === this.subscriptionFrequency.value);
      const subscribed = this.subscriptionToggle.checked && !!plan;
      this.subscriptionToggle.value = plan ? String(plan.id) : '';
      this.querySelector('[data-subscription-details]').hidden = !subscribed;
      this.subscriptionFrequency.hidden = (variant?.plans.length || 0) < 2;
      this.subscriptionFrequency.previousElementSibling.hidden = this.subscriptionFrequency.hidden;
      this.querySelector('[data-subscription-summary]').textContent = subscribed ? `${plan.formattedPrice} — ${plan.name}` : '';
      this.subscriptionLabel.textContent = plan && variant.price > plan.price
        ? this.subscriptionOriginalLabel.replace(/\d+%/, `${Math.round((variant.price - plan.price) / variant.price * 100)}%`)
        : this.subscriptionOriginalLabel;
      this.querySelectorAll('.bp-bundle').forEach(label => {
        const item = this.subscriptionData[label.querySelector('[name="id"]').value];
        const price = label.querySelector('.bp-bundle-price');
        price.dataset.originalPrice ||= price.textContent;
        const allocation = item?.plans[0];
        const cents = subscribed && allocation ? allocation.price : Number(label.dataset.priceCents || item?.price || 0);
        price.textContent = subscribed && allocation ? allocation.formattedPrice : price.dataset.originalPrice;
        const unitPrice = label.querySelector('[data-unit-price]');
        const format = amount => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || window.Shopify?.currency?.active || 'GBP' }).format(amount / 100);
        if (unitPrice) unitPrice.textContent = format(Math.round(cents / Number(label.dataset.bottles || 1)));
        if (label.hasAttribute('data-calculated-savings')) {
          const compare = Number(label.dataset.compareCents || 0);
          const badge = label.querySelector('.bp-bundle-save');
          const previous = label.querySelector('.bp-bundle-pricing s');
          badge.hidden = previous.hidden = compare <= cents;
          previous.textContent = compare > cents ? format(compare) : '';
          badge.textContent = compare > cents ? `SAVE ${Math.round((compare - cents) / compare * 100)}%` : '';
        }
      });
    }
  }
  customElements.define('bp-product-form', BloomProductForm);
  document.addEventListener('click', async (event) => {
    const checkout = event.target.closest('.bp-drawer-checkout');
    if (checkout?.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
    if (checkout && localPreview) { event.preventDefault(); window.location.assign(checkoutUrl()); return; }
    const open = event.target.closest('[data-cart-open]');
    const close = event.target.closest('[data-cart-close]');
    const remove = event.target.closest('[data-cart-remove]');
    const step = event.target.closest('[data-cart-step]');
    const dialog = document.querySelector('#BloomCart');
    if (open) { event.preventDefault(); try { await refreshCart(); dialog.showModal(); } catch (_) { location.href = config.cartUrl; } }
    if (close || event.target === dialog) dialog.close();
    if (step) {
      const row = step.closest('[data-line-key]');
      const quantity = row.querySelector('[data-cart-quantity]');
      await changeCart(row.dataset.lineKey, Math.max(0, Number(quantity.value) + Number(step.dataset.cartStep)));
    }
    if (remove) { event.preventDefault(); await changeCart(remove.closest('[data-line-key]').dataset.lineKey, 0); }
  });
  const quantityUpdates = new Map();
  let activeCartChanges = 0;
  let cartChanges = Promise.resolve();
  function updateCartBusy() {
    const form = document.querySelector('[data-cart-page-form]');
    const busy = activeCartChanges > 0 || quantityUpdates.size > 0;
    if (form) {
      form.setAttribute('aria-busy', String(busy));
      form.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = busy; });
    }
    const drawer = document.querySelector('#BloomCart [data-cart-content]');
    if (drawer) {
      drawer.setAttribute('aria-busy', String(busy));
      drawer.querySelectorAll('[data-cart-step],[data-cart-remove]').forEach(button => { button.disabled = busy; });
      drawer.querySelector('.bp-drawer-checkout')?.setAttribute('aria-disabled', String(busy));
    }
  }
  function queueQuantity(input, delay) {
    const key = input.closest('[data-line-key]').dataset.lineKey;
    clearTimeout(quantityUpdates.get(key));
    quantityUpdates.set(key, setTimeout(() => {
      quantityUpdates.delete(key);
      const quantity = Number(input.value);
      if (Number.isInteger(quantity) && quantity >= 0) changeCart(key, quantity);
      else updateCartBusy();
    }, delay));
    updateCartBusy();
  }
  document.addEventListener('submit', event => {
    if (localPreview && event.target.matches('[data-cart-page-form]') && event.submitter?.name === 'checkout') {
      event.preventDefault();
      if (activeCartChanges === 0 && quantityUpdates.size === 0) window.location.assign(checkoutUrl());
    }
  });
  document.addEventListener('input', event => {
    if (event.target.matches('[data-cart-quantity]')) queueQuantity(event.target, 400);
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-cart-quantity]')) queueQuantity(event.target, 0);
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-cart-quantity]')) {
      event.preventDefault();
      queueQuantity(event.target, 0);
    }
  });
  function changeCart(key, quantity) {
    clearTimeout(quantityUpdates.get(key));
    quantityUpdates.delete(key);
    activeCartChanges++;
    document.querySelectorAll('[data-cart-error]').forEach(error => { error.hidden = true; });
    updateCartBusy();
    cartChanges = cartChanges.then(() => performCartChange(key, quantity));
    return cartChanges;
  }
  async function performCartChange(key, quantity) {
    let succeeded = false;
    let restoreFocus;
    try {
      const response = await fetch(config.cartChangeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      if (!response.ok) throw new Error('Unable to change the cart.');
      const cart = await response.json();
      const updates = {};
      for (const gift of cart.items.filter(item => item.properties?._bp_bonus === '1')) {
        const parentItems = cart.items.filter(item => String(item.product_id) === gift.properties._bp_parent && item.properties?._bp_bonus !== '1');
        const eligibleBottles = bonusEligibleBottles(parentItems);
        if (!parentItems.length || (Number(gift.properties._bp_minimum || 0) > eligibleBottles)) updates[gift.key] = 0;
      }
      if (Object.keys(updates).length) {
        const cleanup = await fetch(`${config.cartUrl}/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
        if (!cleanup.ok) throw new Error('Unable to update the included bonuses.');
      }

      restoreFocus = await refreshCart();
      succeeded = true;
      announce('Cart updated');
    } catch (failure) {
      announce(failure.message);
      document.querySelectorAll('[data-cart-error]').forEach(error => { error.textContent = failure.message; error.hidden = false; });
    } finally {
      activeCartChanges--;
      updateCartBusy();
      restoreFocus?.();
      if (succeeded && activeCartChanges === 0 && quantityUpdates.size === 0 && document.body.classList.contains('template-cart')) location.reload();
    }
  }
})();
document.addEventListener('click', (event) => {
  const arrow = event.target.closest('.wx-rv .pv,.wx-rv .nx');
  if (!arrow) return;
  const reviews = arrow.closest('.wx-rv').querySelector('.rw');
  const card = reviews.querySelector('.rc');
  if (card) reviews.scrollBy({ left: (card.getBoundingClientRect().width + 16) * (arrow.classList.contains('nx') ? 1 : -1), behavior: 'smooth' });
});
function initializeBloomSections() {
  document.querySelectorAll('[data-ship-date]').forEach((label) => {
    const date = new Date(); let days = 0;
    while (days < 2) { date.setDate(date.getDate() + 1); if (date.getDay() !== 0 && date.getDay() !== 6) days++; }
    label.textContent = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
  });

}
initializeBloomSections();
document.addEventListener('shopify:section:load', initializeBloomSections);
// Reference sale clock: midnight in New York, scoped to the announcement bar.
function updateBloomSaleClock() {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(new Date()).map(({type,value}) => [type,value]));
  const values = { h: 23 - Number(parts.hour), m: 59 - Number(parts.minute), s: 59 - Number(parts.second) };
  for (const [unit, value] of Object.entries(values)) document.querySelectorAll(`.wx-bar [data-${unit}]`).forEach((digit) => { digit.textContent = String(value).padStart(2, '0'); });
}
updateBloomSaleClock();
setInterval(updateBloomSaleClock, 1000);
